import { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Tooltip, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, FormGroup } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { regexConstants } from "../../constants/regEx";
import { numberWithCommas } from "../../utils";
import DailyRateWarningModal from "./DailyRateWarningModal";

// Shared Fixed Table (Whichever is greater) with "Enable Daily" toggle, used by Rental Rate & Reserve Rate.
// Toggling only fires onFieldChange("daily_rent", checked); the parent resets fixed_table_condition accordingly.
const FixedTableConditionsTable = ({ data, onFieldChange, error, setError, modeType, rateLabel = "Rental Rate", dailyLabel = "Daily Rent" }) => {
  const [dailyRateWarning, setDailyRateWarning] = useState(false);
  const { leaseBasicDetails } = useSelector((state) => state.leases);
  const isDailyEnabled = !!data?.daily_rent;
  const fixedTableRows = [
    { key: "fixed", label: "Fixed" },
    ...(isDailyEnabled ? [{ key: "daily", label: "Daily" }] : []),
    { key: "fc_rate", label: "Flight Cycles" },
    { key: "fh_rate", label: "Flight Hours" },
  ];
  const validationMessage = isDailyEnabled
    ? "Enter a Daily Rate and at least one of Flight Cycles or Flight Hours."
    : "Enter a Fixed Rate and at least one of Flight Cycles or Flight Hours.";
  const handleDailyToggle = (checked) => {
    const isActiveLease = leaseBasicDetails?.status && leaseBasicDetails?.status !== "Draft";
    if (checked && isActiveLease) {
      setDailyRateWarning(true);
    } else {
      onFieldChange("daily_rent", checked);
    }
  };
  return (
    <Box sx={{ width: "100%" }}>
      <FormGroup className='disabled-checkbox-wrapper'>
        <Box style={{ display: 'flex', alignItems: 'center', marginLeft: "-12px", cursor: modeType === "view" ? "not-allowed" : "pointer" }}>
          <Checkbox
            checked={isDailyEnabled}
            onChange={(e) => handleDailyToggle(e.target.checked)}
            disabled={modeType === 'view'}
            disableRipple
          />
          <span className="check-item-span">Enable {dailyLabel}</span>
        </Box>
      </FormGroup>
      {/* Same offset & row height as the range table label row (Import/Export buttons) so spacing matches "Enable Escalation From Table" */}
      <Box sx={{ display: "flex", alignItems: "center", minHeight: "36.5px", mt: 0.4, mb: 1 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          Fixed Table Conditions
          <Tooltip title={validationMessage}>
            <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
              <InfoIcon fontSize="8px" color="primary" />
            </Box>
          </Tooltip>
        </Typography>
      </Box>
      <TableContainer className='scrollable-table-container'>
        <Table size="small" className="mui-table-format">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: '100px !important' }}> Type</TableCell>
              {fixedTableRows.map((row) => (
                <TableCell key={row.key}>
                  {row.label}{(row.key === 'daily' || (row.key === 'fixed' && !isDailyEnabled)) ? '*' : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell> Rate</TableCell>
              {fixedTableRows.map((row) => {
                const isFixedDisabled = row.key === 'fixed' && isDailyEnabled;
                return (
                  <TableCell key={row.key}>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      variant="outlined"
                      sx={{ maxWidth: 200, mx: "auto" }}
                      value={modeType === 'view' && data?.fixed_table_condition?.[row.key] !== '' && data?.fixed_table_condition?.[row.key] !== null && data?.fixed_table_condition?.[row.key] !== undefined ? numberWithCommas(data?.fixed_table_condition?.[row.key]) : (data?.fixed_table_condition?.[row.key] ?? "")}
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) || e.target.value === "" ? onFieldChange("fixed_table_condition", { [row.key]: e.target.value }) : e.preventDefault(); }}
                      onFocus={() => setError(prev => ({ ...prev, fixed_table_condition: { ...prev?.fixed_table_condition, [row.key]: "" } }))}
                      error={Boolean(error?.fixed_table_condition?.[row.key])}
                      helperText={error?.fixed_table_condition?.[row.key]}
                      disabled={modeType === 'view' || isFixedDisabled}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">{data?.currency?.symbol}</InputAdornment>) } }}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <DailyRateWarningModal
        open={dailyRateWarning}
        rateLabel={rateLabel}
        dailyLabel={dailyLabel}
        onCancel={() => setDailyRateWarning(false)}
        onProceed={() => { onFieldChange("daily_rent", true); setDailyRateWarning(false); }}
      />
    </Box>
  );
};

export default FixedTableConditionsTable;
